// Comments on a file (ADR 0039). The live channel (ADR 0040) marks
// these queries stale when someone else changes a thread; the interval
// below is the fallback when it isn't connected.

import { useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";
import type { CommentEntry, CommentPerson } from "./types";
import { errorDetail } from "@/lib/apiError";

export const commentKeys = {
  all: ["comments"] as const,
  file: (fileId: string) => [...commentKeys.all, fileId] as const,
  candidates: (fileId: string, q: string) =>
    [...commentKeys.all, fileId, "candidates", q] as const,
};

export interface CommentList {
  items: CommentEntry[];
  count: number;
  reactions: string[];
}

export function useComments(fileId: string) {
  return useQuery({
    queryKey: commentKeys.file(fileId),
    refetchInterval: 60_000,
    queryFn: async (): Promise<CommentList> => {
      const { data, error } = await api.GET(
        "/api/v1/files/{file_id}/comments",
        { params: { path: { file_id: fileId } } },
      );
      if (error || !data)
        throw new Error(errorDetail(error, "could not load comments"));
      return data;
    },
  });
}

export function useMentionCandidates(fileId: string, q: string | null) {
  return useQuery({
    queryKey: commentKeys.candidates(fileId, q ?? ""),
    enabled: q !== null,
    staleTime: 30_000,
    queryFn: async (): Promise<CommentPerson[]> => {
      const { data, error } = await api.GET(
        "/api/v1/files/{file_id}/mention-candidates",
        { params: { path: { file_id: fileId }, query: { q: q ?? "" } } },
      );
      if (error || !data)
        throw new Error(errorDetail(error, "could not load people"));
      return data.items;
    },
  });
}

export async function postComment(
  fileId: string,
  body: string,
  parentId: string | null,
): Promise<CommentEntry> {
  const { data, error } = await api.POST("/api/v1/files/{file_id}/comments", {
    params: { path: { file_id: fileId } },
    body: { body, parent_id: parentId },
  });
  if (error || !data)
    throw new Error(errorDetail(error, "could not post comment"));
  return data;
}

export async function editComment(
  commentId: string,
  body: string,
): Promise<CommentEntry> {
  const { data, error } = await api.PATCH("/api/v1/comments/{comment_id}", {
    params: { path: { comment_id: commentId } },
    body: { body },
  });
  if (error || !data)
    throw new Error(errorDetail(error, "could not save comment"));
  return data;
}

export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await api.DELETE("/api/v1/comments/{comment_id}", {
    params: { path: { comment_id: commentId } },
  });
  if (error) throw new Error(errorDetail(error, "could not delete comment"));
}

export async function setReaction(
  commentId: string,
  emoji: string,
  on: boolean,
): Promise<void> {
  const params = { path: { comment_id: commentId, emoji } };
  const { error } = on
    ? await api.PUT("/api/v1/comments/{comment_id}/reactions/{emoji}", {
        params,
      })
    : await api.DELETE("/api/v1/comments/{comment_id}/reactions/{emoji}", {
        params,
      });
  if (error) throw new Error(errorDetail(error, "could not save reaction"));
}

/** Refetch a file's comments after a change. */
export function useRefreshComments(fileId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: commentKeys.file(fileId) });
}
